import { useEffect, useState, useRef } from "react";
import api, { API_BASE } from "../lib/api";
import PageHeader from "../components/PageHeader";
import { toast } from "sonner";
import { Plus, Trash2, Tag, ImagePlus, Pencil, X, Package } from "lucide-react";

const inputCls =
  "w-full bg-white border border-[#E9EDEF] focus:border-[#25D366] focus:ring-1 focus:ring-[#25D366] text-[#111B21] rounded-lg px-3 py-2 text-sm placeholder:text-[#8696A0] focus:outline-none transition-colors";

const empty = { name: "", price: "", details: "", active: true };

export default function Offers() {
  const [offers, setOffers] = useState([]);
  const [form, setForm] = useState(empty);
  const [editingId, setEditingId] = useState(null);
  const [busy, setBusy] = useState(false);
  const imgRef = useRef();
  const [imgFor, setImgFor] = useState(null);

  const load = () => api.get("/offers").then((r) => setOffers(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const reset = () => { setForm(empty); setEditingId(null); };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Offer name is required"); return; }
    setBusy(true);
    try {
      if (editingId) {
        await api.put(`/offers/${editingId}`, form);
        toast.success("Offer updated");
      } else {
        await api.post("/offers", form);
        toast.success("Offer added");
      }
      reset(); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  const edit = (o) => { setEditingId(o.offer_id); setForm({ name: o.name || "", price: o.price || "", details: o.details || "", active: o.active !== false }); };
  const del = async (id) => { if (!window.confirm("Delete this offer?")) return; await api.delete(`/offers/${id}`); toast.success("Deleted"); load(); };
  const toggle = async (o) => { await api.put(`/offers/${o.offer_id}`, { active: !(o.active !== false) }); load(); };

  const pickImage = (id) => { setImgFor(id); imgRef.current?.click(); };
  const uploadImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !imgFor) return;
    const fd = new FormData(); fd.append("file", file);
    try {
      await api.post(`/offers/${imgFor}/image`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Image added");
      load();
    } catch (err) { toast.error("Image upload failed"); }
    finally { if (imgRef.current) imgRef.current.value = ""; setImgFor(null); }
  };

  return (
    <>
      <PageHeader eyebrow="Vision AI" title="Offers & Packages"
        subtitle="The AI uses these to recognise offer screenshots customers send" />
      <input ref={imgRef} type="file" accept="image/*" onChange={uploadImage} className="hidden" data-testid="offer-image-input" />
      <div className="flex-1 min-h-0 overflow-y-auto p-4 md:p-8 grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* List */}
        <div className="lg:col-span-2 border border-[#E9EDEF] bg-white rounded-lg" data-testid="offers-list">
          <div className="px-5 py-4 border-b border-[#E9EDEF] flex items-center justify-between">
            <h3 className="font-heading text-sm font-medium text-[#111B21]">Your Offers</h3>
            <span className="font-mono text-[10px] text-[#8696A0]">{offers.length}</span>
          </div>
          <div className="divide-y divide-[#E9EDEF]">
            {offers.length === 0 && (
              <p className="px-5 py-10 text-center text-sm text-[#8696A0]">
                <Package className="h-5 w-5 mx-auto mb-2 text-[#54656F]" />
                No offers yet. Add your packages so the AI can match offer screenshots.
                <br />If none are added, the AI falls back to your Knowledge Base.
              </p>
            )}
            {offers.map((o) => (
              <div key={o.offer_id} className="flex items-start gap-3 px-5 py-3" data-testid={`offer-row-${o.offer_id}`}>
                {o.image_path ? (
                  <img src={`${API_BASE}/assets/file/${o.image_path}`} alt="" className="h-12 w-12 rounded-lg object-cover flex-shrink-0 border border-[#E9EDEF]" />
                ) : (
                  <div className="h-12 w-12 rounded-lg bg-[#E7FCE8] flex items-center justify-center flex-shrink-0"><Tag className="h-5 w-5 text-[#075E54]" /></div>
                )}
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-sm font-semibold text-[#111B21] truncate">{o.name}</p>
                    {o.price && <span className="font-mono text-[11px] text-[#075E54] bg-[#E7FCE8] px-1.5 py-0.5 rounded-md">{o.price}</span>}
                    <button onClick={() => toggle(o)} data-testid={`offer-toggle-${o.offer_id}`}
                      className={`font-mono text-[9px] px-1.5 py-0.5 rounded-md border ${o.active !== false ? "bg-[#DCF8C6] text-[#075E54] border-[#25D366]/40" : "bg-[#F0F2F5] text-[#8696A0] border-[#E9EDEF]"}`}>
                      {o.active !== false ? "ACTIVE" : "INACTIVE"}
                    </button>
                  </div>
                  {o.details && <p className="text-xs text-[#54656F] mt-1 whitespace-pre-wrap break-words">{o.details}</p>}
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  <button onClick={() => pickImage(o.offer_id)} title="Upload image" data-testid={`offer-image-${o.offer_id}`} className="text-[#8696A0] hover:text-[#075E54] p-1"><ImagePlus className="h-4 w-4" /></button>
                  <button onClick={() => edit(o)} title="Edit" data-testid={`offer-edit-${o.offer_id}`} className="text-[#8696A0] hover:text-[#075E54] p-1"><Pencil className="h-4 w-4" /></button>
                  <button onClick={() => del(o.offer_id)} title="Delete" data-testid={`offer-delete-${o.offer_id}`} className="text-[#8696A0] hover:text-red-600 p-1"><Trash2 className="h-4 w-4" /></button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Form */}
        <div className="border border-[#E9EDEF] bg-white rounded-lg p-5 space-y-3 h-fit">
          <div className="flex items-center justify-between">
            <h3 className="font-heading text-sm font-medium text-[#111B21]">{editingId ? "Edit Offer" : "Add Offer"}</h3>
            {editingId && <button onClick={reset} className="text-[#8696A0] hover:text-[#111B21]" data-testid="offer-cancel-edit"><X className="h-4 w-4" /></button>}
          </div>
          <input data-testid="offer-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Offer name (e.g. Bhajan Premium Pack)" className={inputCls} />
          <input data-testid="offer-price" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} placeholder="Price (e.g. ₹4999)" className={inputCls} />
          <textarea data-testid="offer-details" rows={6} value={form.details} onChange={(e) => setForm({ ...form, details: e.target.value })}
            placeholder="What's included, delivery time, terms…" className={inputCls} />
          <label className="flex items-center gap-2 text-sm text-[#54656F]">
            <input type="checkbox" data-testid="offer-active" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} className="accent-[#25D366]" />
            Active (visible to AI matching)
          </label>
          <button data-testid="offer-save" onClick={save} disabled={busy}
            className="w-full flex items-center justify-center gap-2 bg-[#25D366] text-[#111B21] hover:bg-[#20BD5A] rounded-lg font-medium px-4 py-2 text-sm transition-colors disabled:opacity-50">
            <Plus className="h-4 w-4" /> {editingId ? "Save Changes" : "Add Offer"}
          </button>
          <p className="text-[11px] text-[#8696A0]">Tip: adding an image makes screenshot matching far more accurate. Without one, the AI still matches by name and details.</p>
        </div>
      </div>
    </>
  );
}
